import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { FileImage } from "@/components/ui/file-image";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  initials, ageFromDob, fmtDate, fmtMoney, fmtTime, PRIORITY_STYLES, SESSION_STATUS_STYLES
} from "@/lib/helpers";
import { ArrowLeft, Phone, MapPin, User, CheckCircle, PencilSimple, SealCheck, XCircle } from "@phosphor-icons/react";
import { NewStudentDialog } from "@/pages/StudentsPage";
import StudentCrmTab from "@/components/StudentCrmTab";
import StudentTherapyPlansTab from "@/components/StudentTherapyPlansTab";
import { useAuth } from "@/contexts/AuthContext";

const ENROL_STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  not_registered: "bg-slate-100 text-slate-700 border-slate-200",
  registered: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const ENROL_STATUS_LABEL = {
  pending: "Pending",
  not_registered: "Not registered",
  registered: "Registered",
};

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeRole } = useAuth();
  const [student, setStudent] = useState(null);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [timeline, setTimeline] = useState({ sessions: [], invoices: [], attendance: [] });
  const [branches, setBranches] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusModal, setStatusModal] = useState(null); // null | "registered" | "not_registered"
  const [statusReason, setStatusReason] = useState("");

  const load = async () => {
    try {
      const [s, tt, tl, br] = await Promise.all([
        api.get(`/students/${id}`),
        api.get("/therapy-types"),
        api.get(`/students/${id}/timeline`),
        api.get("/branches"),
      ]);
      setStudent(s.data);
      setTherapyTypes(tt.data || []);
      setTimeline(tl.data || {});
      setBranches(br.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [id]);

  // Iter-45 — allow the Attendance page (or any other surface) to deep-
  // link into edit mode via `?edit=1`. When a user landing here has
  // edit rights, auto-open the edit dialog once and clear the flag from
  // the URL so a manual close doesn't re-open on refresh.
  const [autoEditHandled, setAutoEditHandled] = useState(false);
  useEffect(() => {
    if (autoEditHandled) return;
    if (!student || !activeRole) return;
    if (searchParams.get("edit") !== "1") return;
    if (["owner", "admin", "admin_student", "reception"].includes(activeRole)) {
      setShowEdit(true);
    }
    setAutoEditHandled(true);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [student, activeRole, autoEditHandled, searchParams, setSearchParams]);

  if (!student) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const therapies = therapyTypes.filter(t => (student.therapies || []).includes(t.id));
  const branch = branches.find(b => b.id === student.branch_id);
  const primary = student.guardians?.[0];
  const canEdit = ["owner","admin","reception"].includes(activeRole);
  const canOnboard = ["owner","admin","reception","therapist"].includes(activeRole);
  const enrolStatus = student.enrolment_status || "registered";

  const markStatus = async () => {
    if (!statusModal) return;
    const kind = statusModal;
    setStatusBusy(true);
    try {
      await api.post(`/students/${student.id}/mark-${kind === "registered" ? "registered" : "not-registered"}`, { reason: statusReason.trim() || null });
      toast.success(kind === "registered" ? "Student marked Registered" : "Student marked Not Registered");
      setStatusModal(null);
      setStatusReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStatusBusy(false); }
  };

  const openStatusModal = (kind) => {
    setStatusReason("");
    setStatusModal(kind);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 -ml-2"><ArrowLeft size={14} /> Back</Button>
        <div className="flex items-center gap-2">
          {canOnboard && enrolStatus !== "registered" && (
            <Button size="sm" onClick={() => openStatusModal("registered")} disabled={statusBusy}
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="mark-registered-btn"
            >
              <SealCheck size={14} weight="fill" /> Mark registered
            </Button>
          )}
          {canOnboard && enrolStatus === "pending" && (
            <Button size="sm" variant="outline" onClick={() => openStatusModal("not_registered")} disabled={statusBusy}
              className="gap-1.5 text-slate-700"
              data-testid="mark-not-registered-btn"
            >
              <XCircle size={14} /> Mark not registered
            </Button>
          )}
          {canEdit && (
            <Button size="sm" onClick={() => setShowEdit(true)} className="gap-1.5" data-testid="edit-student-btn">
              <PencilSimple size={14} weight="bold" /> Edit profile
            </Button>
          )}
        </div>
      </div>

      {/* Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <Avatar className="h-24 w-24 border-2 border-primary/10">
              {student.photo_file_id ? (
                <FileImage
                  fileId={student.photo_file_id}
                  alt={student.name}
                  className="h-full w-full object-cover"
                  fallback={<AvatarFallback className="bg-primary/10 text-primary font-display font-bold text-3xl">{initials(student.name)}</AvatarFallback>}
                />
              ) : (
                <AvatarFallback className="bg-primary/10 text-primary font-display font-bold text-3xl">{initials(student.name)}</AvatarFallback>
              )}
            </Avatar>
            <div className="flex-1 space-y-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-3xl font-bold tracking-tight">{student.name}</h1>
                  <Badge className={PRIORITY_STYLES[student.priority || "normal"]}>{student.priority}</Badge>
                  <Badge className={`${ENROL_STATUS_STYLES[enrolStatus]} border`} data-testid="enrol-status-badge">
                    {ENROL_STATUS_LABEL[enrolStatus]}
                  </Badge>
                </div>
                {/* iter-67 — Student ID pill sits right under the name.
                    Click-to-copy for admins who paste it into notes /
                    emails. Only rendered when the student actually has
                    an ID (auto-gen may be off for a workspace). */}
                {student.student_id && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(student.student_id);
                      toast.success(`Copied ${student.student_id}`);
                    }}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-primary/10 hover:bg-primary/15 transition-colors px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-primary"
                    data-testid="student-id-pill"
                    title="Click to copy"
                  >
                    ID · {student.student_id}
                  </button>
                )}
                <div className="text-sm text-muted-foreground mt-1">
                  {ageFromDob(student.dob)} · {student.gender || "—"} · {branch?.name || "—"}
                </div>
              </div>

              <div className="text-sm">{student.diagnosis && <span className="font-medium">{student.diagnosis}</span>}</div>

              <div className="flex flex-wrap gap-1.5">
                {therapies.map(t => (
                  <Badge key={t.id} variant="outline" style={{ borderColor: `${t.color}66`, color: t.color }} className="font-medium">{t.name}</Badge>
                ))}
              </div>

              {primary && (
                <div className="flex flex-wrap gap-4 pt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><User size={13} /> {primary.name} ({primary.relation})</span>
                  <span className="flex items-center gap-1.5"><Phone size={13} /> {primary.phone}</span>
                  {student.address && <span className="flex items-center gap-1.5"><MapPin size={13} /> {student.address}</span>}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="sessions">
        <TabsList>
          <TabsTrigger value="sessions">Sessions ({timeline.sessions?.length || 0})</TabsTrigger>
          <TabsTrigger value="attendance">Attendance ({timeline.attendance?.length || 0})</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({timeline.invoices?.length || 0})</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Therapy Plans</TabsTrigger>
          <TabsTrigger value="crm" data-testid="tab-crm">CRM</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions" className="mt-4">
          <Card><CardContent className="p-0">
            {timeline.sessions?.length === 0 ?
              <div className="p-8 text-center text-sm text-muted-foreground">No sessions yet.</div> :
              <div className="divide-y divide-border">
                {timeline.sessions.map(s => {
                  const tt = therapyTypes.find(t => t.id === s.therapy_type_id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => navigate(`/sessions/${s.id}`)}
                      className="w-full px-5 py-3 flex items-center gap-3 hover:bg-muted/40 text-left"
                    >
                      <div className="h-9 w-9 rounded-md flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: tt?.color || "#4A6753" }}>
                        {tt?.code || "—"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{tt?.name || "Session"}</div>
                        <div className="text-xs text-muted-foreground">{fmtDate(s.created_at)} · {fmtTime(s.started_at || s.created_at)}</div>
                      </div>
                      <Badge className={SESSION_STATUS_STYLES[s.status]}>{s.status.replace("_", " ")}</Badge>
                    </button>
                  );
                })}
              </div>
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="attendance" className="mt-4">
          <Card><CardContent className="p-0">
            {timeline.attendance?.length === 0 ?
              <div className="p-8 text-center text-sm text-muted-foreground">No attendance recorded.</div> :
              <div className="divide-y divide-border">
                {timeline.attendance.map(a => (
                  <div key={a.id} className="px-5 py-3 flex items-center gap-3">
                    <CheckCircle size={16} className="text-primary" weight="duotone" />
                    <div className="flex-1 text-sm">
                      <div className="font-medium">{fmtDate(a.date)}</div>
                      <div className="text-xs text-muted-foreground">In {fmtTime(a.time_in)} {a.time_out && `· Out ${fmtTime(a.time_out)}`}</div>
                    </div>
                    <Badge variant="outline">{a.status}</Badge>
                  </div>
                ))}
              </div>
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Card><CardContent className="p-0">
            {timeline.invoices?.length === 0 ?
              <div className="p-8 text-center text-sm text-muted-foreground">No invoices yet.</div> :
              <div className="divide-y divide-border">
                {timeline.invoices.map(inv => (
                  <button key={inv.id} onClick={() => navigate(`/billing/${inv.id}`)} className="w-full px-5 py-3 flex items-center gap-3 hover:bg-muted/40 text-left">
                    <div className="flex-1"><div className="text-sm font-medium">{inv.invoice_no}</div><div className="text-xs text-muted-foreground">{fmtDate(inv.created_at)}</div></div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">{fmtMoney(inv.total)}</div>
                      <Badge className={inv.status==="paid"?"bg-emerald-100 text-emerald-800":inv.status==="partial"?"bg-amber-100 text-amber-800":"bg-red-100 text-red-800"}>{inv.status}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="crm" className="mt-4">
          <StudentCrmTab studentId={student.id} />
        </TabsContent>

        <TabsContent value="plans" className="mt-4">
          <StudentTherapyPlansTab studentId={student.id} />
        </TabsContent>

        <TabsContent value="profile" className="mt-4 space-y-4">
          <Card><CardHeader><CardTitle className="font-display text-base tracking-tight">Medical notes</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-muted-foreground whitespace-pre-line">{student.medical_notes || "—"}</p></CardContent>
          </Card>
          <Card><CardHeader><CardTitle className="font-display text-base tracking-tight">Guardians</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {student.guardians?.length ? student.guardians.map((g, i) => (
                <div key={`${g.phone || g.email || g.name || "g"}-${i}`} className="flex items-center gap-3 p-3 rounded-md border border-border">
                  <Avatar className="h-9 w-9"><AvatarFallback className="bg-accent/20 text-accent font-semibold">{initials(g.name)}</AvatarFallback></Avatar>
                  <div className="flex-1 text-sm"><div className="font-medium">{g.name}</div><div className="text-xs text-muted-foreground">{g.relation} · {g.phone}</div></div>
                </div>
              )) : <div className="text-sm text-muted-foreground">No guardians on file.</div>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <NewStudentDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        therapyTypes={therapyTypes}
        branches={branches}
        student={student}
        onCreated={() => { setShowEdit(false); load(); }}
      />

      <Dialog open={!!statusModal} onOpenChange={(v) => { if (!v) setStatusModal(null); }}>
        <DialogContent className="bg-card max-w-md" data-testid="mark-status-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {statusModal === "registered" ? "Mark student as Registered" : "Mark student as Not Registered"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {statusModal === "registered"
              ? "Student will move to Registered — they'll be included in the active roster and billing runs."
              : "Student stays visible but is flagged Not Registered — usually while a registration fee is pending."}
          </p>
          <div className="space-y-1.5">
            <Label>
              {statusModal === "registered" ? "Optional note" : "Reason"}{" "}
              <span className="text-muted-foreground text-[11px]">
                {statusModal === "registered" ? "(saved to audit log)" : "(shown on the student profile)"}
              </span>
            </Label>
            <Textarea rows={3}
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder={statusModal === "registered" ? "e.g. Paid registration fee, first consult booked" : "e.g. Registration fee outstanding"}
              data-testid="mark-status-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModal(null)} disabled={statusBusy}>Cancel</Button>
            <Button onClick={markStatus} disabled={statusBusy}
              className={statusModal === "registered" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
              data-testid="mark-status-confirm"
            >
              {statusBusy ? "Saving…" : (statusModal === "registered" ? "Mark Registered" : "Mark Not Registered")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
